import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  input,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ENVIRONMENT, ModalService, PageSectionBuildingModule, SharedService, UserService } from '@cfp/core';
import { BuildingMeasureSidePanel } from '@cfp/domains/measure/side-panel';
import { CrremBuildingParisPathway } from '@cfp/features-shared/crrem';
import { BuildingMeasureTableItemDTOWithYearData, YearData } from '@cfp/features-shared/interfaces/year-data';
import { WeiiRoadmapChart } from '@cfp/features-shared/weii';
import { BuildingNotificationComponent } from '@cfp/shared/building/notification';
import { EnergyLabel, shouldShowImprovedEnergyLabel } from '@cfp/shared/energy-label';
import { MeasureUpdateModal } from '@cfp/shared/measure/update-modal';
import { CachedRoadmapService, RoadmapService } from '@cfp/shared/roadmap';
import { ROADMAP_IMPLEMENTATION_YEARS_PROPERTY_END } from '@cfp/shared/roadmap/const';
import { LabelGroupModule, LabelGroupService } from '@cfp/shared/ui/label-group';
import {
  ChartDataTableSidePanel,
  ChartDataTableSidePanelData,
  Co2Module,
  Combobox,
  LiveRegionStatusService,
  OrderByPipe
} from '@cfp/ui';
import { AlertModalResult, AlertService, InfoModal } from '@cfp/ui/feedback';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  BuildingDetailsDTO,
  BuildingEnergyConsumptionService,
  BuildingEnergyConsumptionYearDTO,
  BuildingLabelService,
  BuildingMeasureTableItemDTO,
  BuildingMeasureTypeDTO,
  BuildingMeasuresListService,
  BuildingMeasuresService,
  BuildingNotificationTypeDTO,
  BuildingRoadmapAIEstimationDTO,
  BuildingsService,
  EnergyLabelTypeDTO,
  IndexTypeDTO,
  LabelGroupDTO,
  MeasureListDTO,
  MeasureStatusDTO,
  RoadmapService as RoadmapApiService,
  RoadmapBuildingDetailsDTO,
  RoadmapBuildingYearDTO,
  UserDefinedLabelYearDTO
} from 'csr-asset-app';
import { Observable, Subscription, catchError, finalize, map, mergeMap, of } from 'rxjs';
import { RoadmapChart, RoadmapChartUsage, mapBuildingDetailYearResultsNumbers } from '../../charts';
import { RoadmapTable } from '../../components/roadmap-table';
import { RoadmapMeasureRelationAlert } from '../roadmap-measure-relation-alert';

type BuildingMeasureTableItemWithIndex = BuildingMeasureTableItemDTO & { index: number };

@Component({
  selector: 'cfp-roadmap-building-content',
  templateUrl: './roadmap-building-content.html',
  imports: [
    PageSectionBuildingModule,
    RoadmapTable,
    RoadmapChart,
    RoadmapChartUsage,
    Combobox,
    EnergyLabel,
    CrremBuildingParisPathway,
    TranslateModule,
    NgClass,
    LabelGroupModule,
    BuildingNotificationComponent,
    WeiiRoadmapChart,
    RoadmapMeasureRelationAlert
  ],
  providers: [OrderByPipe, Co2Module],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoadmapBuildingContent implements OnInit, OnDestroy {
  protected readonly environment = inject(ENVIRONMENT);

  public readonly buildingId = input<number>();
  public readonly buildingFromInput = input<BuildingDetailsDTO>(undefined, { alias: 'building' });
  public readonly portfolioId = input<number>();
  public readonly buildingPublicSharedId = input<string | undefined>(undefined);
  public readonly showMeasureDetailsButtonSidePanel = input<boolean>(
    this.environment.components.showMeasureDetailsButtonSidePanel ?? true
  );
  public readonly showAddMeasure = input<boolean>(true);
  public readonly showDefaultTitle = input<boolean>(true);
  public readonly showImplementedMeasuresToggle = input<boolean>(true);
  public readonly showEnergyLabelInRow = input<boolean>(false);
  public readonly showCharts = input<boolean>(true);

  protected readonly building = signal<BuildingDetailsDTO | undefined>(undefined);
  protected readonly details = signal<RoadmapBuildingDetailsDTO | undefined>(undefined);
  protected readonly years = signal<Partial<RoadmapBuildingYearDTO>[]>([]);
  protected readonly measures = signal<BuildingMeasureTableItemDTOWithYearData[]>([]);
  protected readonly displayStartYear = signal<number | undefined>(undefined);
  protected readonly loading = signal<boolean>(false);
  protected readonly loadingYears = signal<boolean>(false);
  protected readonly saving = signal<boolean>(false);
  protected readonly errorMessage = signal<string | undefined>(undefined);

  protected readonly measuresOutOfScope = signal<BuildingMeasureTableItemDTOWithYearData[] | undefined>(undefined);
  protected readonly totals = signal<YearData | undefined>(undefined);
  protected readonly hasUserDefinedMeasures = signal(false);
  protected readonly hasPlannedMeasures = signal(false);
  protected readonly hasPlannedMeasuresWithoutYear = signal(false);
  protected readonly intro = signal<string | undefined>(undefined);
  protected readonly hasAllMeasureStatuses = signal(true);
  protected readonly showM2 = signal(false);
  protected readonly resetSort = signal(false);
  protected readonly startEnergyConsumptionYear = signal<number | undefined>(undefined);

  protected readonly BuildingNotificationTypeDTO = BuildingNotificationTypeDTO;
  protected readonly LabelGroupDTO = LabelGroupDTO;

  private readonly buildingMeasureService = inject(BuildingMeasuresService);
  private readonly alertService = inject(AlertService);
  private readonly orderByPipe = inject(OrderByPipe);
  private readonly translateService = inject(TranslateService);
  private readonly roadmapApiService = inject(RoadmapApiService);
  private readonly cachedRoadmapService = inject(CachedRoadmapService);
  private readonly roadmapService = inject(RoadmapService);
  private readonly buildingMeasuresListService = inject(BuildingMeasuresListService);
  private readonly buildingsService = inject(BuildingsService);
  private readonly modalService = inject(ModalService);
  private readonly buildingEnergyConsumptionService = inject(BuildingEnergyConsumptionService);
  private readonly liveRegionStatusService = inject(LiveRegionStatusService);
  private readonly userService = inject(UserService);
  private readonly sharedService = inject(SharedService);
  private readonly labelGroupService = inject(LabelGroupService);
  private readonly buildingLabelService = inject(BuildingLabelService);

  protected readonly hasCrremLicense = toSignal(this.userService.hasLicense('crrem'));
  protected readonly showMeasureRelationAlert = this.environment.components.showMeasureRelationAlert;
  protected readonly labelGroup = this.labelGroupService.currentLabelGroup;

  protected readonly showImprovedLabel = computed(() => {
    const labelGroup = this.labelGroup();
    if (!labelGroup) {
      return false;
    }
    return shouldShowImprovedEnergyLabel(this.details()?.energyLabels[labelGroup]?.labelSituationType);
  });

  protected readonly showImprovedLabelPlaceholder = computed(() => {
    return this.labelGroup() === LabelGroupDTO.EPC && !this.showImprovedLabel();
  });

  protected readonly canChangeUserDefinedEnergyLabel = computed(() => {
    if (!this.environment.components.canManuallyChangeLabel) {
      return false;
    }
    if (!this.userService.hasScope('buildings-energylabel-modify')) {
      return false;
    }

    const currentEnergyLabel = this.details()?.energyLabels[LabelGroupDTO.EPC]?.currentEnergyLabel;
    if (!currentEnergyLabel) {
      return false;
    }

    // Currently we only support EP2 and EI for user defined energy labels
    return currentEnergyLabel.indexType === IndexTypeDTO.EP2 || currentEnergyLabel.indexType === IndexTypeDTO.EI;
  });

  protected readonly canResetUserDefinedEnergyLabel = computed(() => {
    const canChangeUserDefinedEnergyLabel = this.canChangeUserDefinedEnergyLabel();
    const labelGroup = this.labelGroup();
    if (!canChangeUserDefinedEnergyLabel || !labelGroup) {
      return false;
    }
    return this.years().some((year) => year.energyLabels?.[labelGroup]?.labelType === EnergyLabelTypeDTO.USERDEFINED);
  });

  private readonly reloadComponentSubscription: Subscription;
  private readonly skipNextReloadComponent = signal<boolean>(false);

  protected readonly predicting = signal<boolean>(false);
  protected readonly hasPredictedData = signal<boolean>(false);
  protected readonly overallSummary = signal<string | undefined>(undefined);
  protected readonly changedEnergyLabels = signal<UserDefinedLabelYearDTO[]>([]);
  protected readonly inChangeEnergyLabelMode = signal<boolean>(false);
  private readonly userDefinedEnergyLabelsFixedIndexType = IndexTypeDTO.EP2; // For now we only support EP2 for user defined energy labels

  public constructor() {
    if (this.environment.hasFilteredCompletedMeasures) {
      this.hasAllMeasureStatuses.set(false);
    }

    this.reloadComponentSubscription = this.sharedService.reloadComponentObservable.subscribe(() => {
      if (this.skipNextReloadComponent()) {
        this.skipNextReloadComponent.set(false);
        return;
      }

      const building = this.building();
      if (!this.saving() && building) {
        this.getData(building.id);
      }
    });

    effect(() => {
      const buildingFromInput = this.buildingFromInput();
      if (!buildingFromInput) {
        return;
      }
      this.building.set(buildingFromInput);
    });

    effect(() => {
      const buildingId = this.buildingId();
      if (!buildingId) {
        return;
      }
      this.resetSort.set(true);
    });

    effect(() => {
      const building = this.building();
      if (!building) {
        return;
      }
      this.getData(building.id);
    });
  }

  public ngOnInit(): void {
    this.init();
  }

  public ngOnDestroy(): void {
    this.reloadComponentSubscription.unsubscribe();
  }

  protected onShowData(data: ChartDataTableSidePanelData): void {
    this.modalService.showSidePanel(ChartDataTableSidePanel, { data: data });
  }

  protected onCellClick(event: any): void {
    let measure: BuildingMeasureTableItemDTOWithYearData = event.row;
    if (measure.cols?.[event.year.year]) {
      if (measure.buildingMeasureType === BuildingMeasureTypeDTO.USERDEFINED) {
        this.addOrEditUserDefiniedBuildingMeasure(measure);
      } else {
        this.editBuildingMeasure(measure);
      }
    } else {
      this.planMeasure(measure, event.year.year, event.property);
    }
  }

  protected init(): void {
    const introKey: string = 'roadmap.buildingIntro';
    const intro = this.translateService.instant(introKey);
    if (intro !== introKey && intro !== 'intentionally-blank') {
      this.intro.set(intro);
    }

    const currentYear: number = new Date().getFullYear();
    const displayStart: number = this.roadmapService.getStartYear();
    const years: Partial<RoadmapBuildingYearDTO>[] = [];
    for (let i = displayStart; i <= ROADMAP_IMPLEMENTATION_YEARS_PROPERTY_END; i++) {
      if (i === currentYear) {
        years.push({ year: undefined });
      }
      years.push({ year: i });
    }
    this.years.set(years);
  }

  private editBuildingMeasure(measure: BuildingMeasureTableItemDTO): void {
    const modelRef = this.modalService.show(MeasureUpdateModal, {
      ignoreBackdropClick: true,
      initialState: {
        building: this.building(),
        buildingId: this.buildingId(),
        buildingMeasureId: measure.id,
        showImplementationYear: false,
        buildingCurrencyCode: measure.currencyCode,
        showImpact: this.environment.components.canChangeImpactScore === true,
        labelGroup: this.labelGroup()
      }
    });

    if (modelRef.content) {
      modelRef.content.onClose.subscribe((shouldReload: boolean) => {
        const buildingId = this.buildingId();
        if (shouldReload && buildingId) {
          this.getData(buildingId);
        }
      });
    }
  }

  private planMeasure(
    buildingMeasure: BuildingMeasureTableItemDTO,
    implementationYear: number,
    property: string
  ): void {
    this.saving.set(true);
    this.skipNextReloadComponent.set(true);
    this.roadmapService
      .saveImplementationYear(
        this.roadmapService.mapToBuildingMeasureDetailsDTO(buildingMeasure),
        implementationYear,
        undefined,
        false
      )
      .subscribe({
        next: (isSaved: boolean) => {
          if (isSaved) {
            const oldImplementationYear = buildingMeasure.implementationYear;
            this.updateLiveRegionStatusForCellClick(
              implementationYear,
              oldImplementationYear,
              buildingMeasure[property]
            );
            this.applyData().subscribe();
          }
        },
        error: () => {
          this.saving.set(false);
          this.skipNextReloadComponent.set(false);
        }
      });
  }

  private async processEstimation(
    estimation: BuildingRoadmapAIEstimationDTO,
    measures: BuildingMeasureTableItemDTOWithYearData[],
    totals: YearData | Record<string, any>
  ): Promise<{
    updatedMeasures: BuildingMeasureTableItemDTOWithYearData[];
    updatedTotals: Record<string, any>;
    overallSummary: any;
  }> {
    const { AIService } = await import('@cfp/features-shared/ai');
    const localInjector = Injector.create({
      providers: [{ provide: AIService, useClass: AIService }]
    });

    const aiService = localInjector.get(AIService);
    return aiService.processEstimation(estimation, measures, totals);
  }

  public predictWithAI(estimation: BuildingRoadmapAIEstimationDTO): void {
    this.processEstimation(estimation, this.measures(), this.totals() ?? {}).then((result) => {
      this.measures.set(result.updatedMeasures);
      this.totals.set(result.updatedTotals);
      this.overallSummary.set(result.overallSummary);
      this.hasPredictedData.set(true);
    });
  }

  protected updateLiveRegionStatusForCellClick(
    year: number | undefined,
    oldYear: number | undefined,
    value: any
  ): void {
    let key = year
      ? 'roadmap.ariaLiveRegion.clickShowMeasureTableRow'
      : 'roadmap.ariaLiveRegion.clickHideMeasureTableRow';
    this.liveRegionStatusService.updateStatus(
      this.translateService.instant(key, { year: year ?? oldYear ?? '', value: value })
    );
  }

  protected onMeasureClick(measure: BuildingMeasureTableItemWithIndex): void {
    if (!measure) {
      return;
    }

    if (measure.buildingMeasureType === BuildingMeasureTypeDTO.USERDEFINED) {
      this.addOrEditUserDefiniedBuildingMeasure(measure);
    } else {
      this.modalService.showSidePanel(BuildingMeasureSidePanel, {
        initialState: {
          portfolioId: this.portfolioId(),
          buildingId: this.buildingId(),
          buildingMeasureIndex: measure.index,
          showHeaderLink: this.showMeasureDetailsButtonSidePanel(),
          buildingMeasureIds: this.measures().map((measure) => measure.id)
        },
        class: 'modal-dialog-scrollable modal-dialog-slideout modal-sm',
        ignoreBackdropClick: true,
        keyboard: false
      });
    }
  }

  protected onMeasureStatusUpdate(measureBuildingStatus: {
    newStatus: MeasureStatusDTO;
    buildingMeasure: BuildingMeasureTableItemDTO;
  }) {
    const { newStatus, buildingMeasure } = measureBuildingStatus;

    const oldStatus = buildingMeasure.status;
    const portfolioId = this.portfolioId();
    const buildingId = this.buildingId();
    if (!oldStatus || !portfolioId || !buildingId) {
      return;
    }

    this.roadmapService
      .saveMeasureStatus(
        this.roadmapService.mapToBuildingMeasureDetailsDTO(buildingMeasure),
        newStatus,
        oldStatus,
        portfolioId,
        buildingId
      )
      .subscribe({
        next: (isSaved: boolean) => {
          buildingMeasure.status = isSaved ? newStatus : oldStatus;
          this.getData(buildingId);
        }
      });
  }

  protected showMeasureRelationAlertModal(): void {
    this.modalService.show(InfoModal, {
      initialState: {
        title: 'roadmap.measureRelationAlert.modalTitle',
        body: 'roadmap.measureRelationAlert.modalBody'
      }
    });
  }

  protected addMeasure(measureToAdd: BuildingMeasureTableItemDTOWithYearData): void {
    this.saving.set(true);
    this.buildingMeasureService
      .v2BuildingmeasuresIdStatusPost({ status: MeasureStatusDTO.PLANNED }, measureToAdd.id)
      .subscribe({
        next: () => {
          measureToAdd.status = MeasureStatusDTO.PLANNED;

          this.measures.update((measures) => [...measures, measureToAdd]);

          this.measuresOutOfScope.update((measuresOutOfScope) =>
            this.removeFromArray(measuresOutOfScope ?? [], 'id', measureToAdd.id)
          );
          this.getYears().subscribe();
          this.initMeasures(this.measures());
          this.saving.set(false);
        },
        error: () => {
          this.alertService.showAlert('error', 'error.general');
          this.saving.set(false);
        }
      });
  }

  protected addNewMeasure(): void {
    this.addOrEditUserDefiniedBuildingMeasure(undefined);
  }

  protected updateManualEnergyLabel(year: number, label: string | null): void {
    if (this.labelGroup() !== LabelGroupDTO.EPC) {
      // For now we only support EPC, actually only EP2
      return;
    }

    const changedEnergyLabels = this.changedEnergyLabels().filter((x) => x.year !== year);

    if (label !== null) {
      changedEnergyLabels.push({ year, label });
    }

    changedEnergyLabels.sort((a, b) => a.year - b.year);
    this.changedEnergyLabels.set(changedEnergyLabels);
  }

  protected resetEnergyLabels(): void {
    const buildingId = this.buildingId();
    if (!buildingId || this.labelGroup() !== LabelGroupDTO.EPC) {
      return;
    }

    this.alertService.showConfirmation('warning', 'roadmap.changeEnergyLabels.confirmReset').subscribe((result) => {
      if (result === AlertModalResult.CONFIRM) {
        this.saving.set(true);
        this.buildingLabelService
          .v2BuildingsIdLabelsIndexTypeYearsDelete(buildingId, this.userDefinedEnergyLabelsFixedIndexType)
          .pipe(mergeMap(() => this.applyData()))
          .subscribe({
            next: () => {
              this.inChangeEnergyLabelMode.set(false);
              this.changedEnergyLabels.set([]);
            },
            error: () => {
              this.alertService.showAlert('error', 'error.general');
              this.saving.set(false);
            }
          });
      }
    });
  }

  protected cancelEnergyLabels(): void {
    this.inChangeEnergyLabelMode.set(false);
    this.changedEnergyLabels.set([]);
  }

  protected editEnergyLabels(): void {
    const labelGroup = this.labelGroup();
    if (!labelGroup) {
      return;
    }

    this.inChangeEnergyLabelMode.set(true);
    this.changedEnergyLabels.set(
      this.years().flatMap((roadmapBuildingYear) => {
        const year = roadmapBuildingYear.year;
        const label = roadmapBuildingYear.energyLabels?.[labelGroup]?.label;
        if (!year || !label) {
          return [];
        }
        return [{ year: year, label }];
      })
    );
  }

  protected saveEnergyLabels(): void {
    const buildingId = this.buildingId();
    if (!buildingId) {
      return;
    }

    if (this.labelGroup() !== LabelGroupDTO.EPC || this.changedEnergyLabels().length === 0) {
      this.inChangeEnergyLabelMode.set(false);
      this.changedEnergyLabels.set([]);
      return;
    }

    const labelsToSave = [...this.changedEnergyLabels()];

    this.saving.set(true);
    this.buildingLabelService
      .v2BuildingsIdLabelsIndexTypeYearsPut(labelsToSave, buildingId, this.userDefinedEnergyLabelsFixedIndexType)
      .pipe(mergeMap(() => this.applyData()))
      .subscribe({
        next: () => {
          this.inChangeEnergyLabelMode.set(false);
          this.changedEnergyLabels.set([]);
        },
        error: () => {
          this.alertService.showAlert('error', 'error.general');
          this.saving.set(false);
        }
      });
  }

  private addOrEditUserDefiniedBuildingMeasure(measure: BuildingMeasureTableItemDTO | undefined) {
    const buildingId = this.buildingId();
    if (!buildingId) {
      return;
    }

    const modelRef = this.modalService.show(MeasureUpdateModal, {
      ignoreBackdropClick: true,
      initialState: {
        buildingId: buildingId,
        building: this.building(),
        buildingMeasureId: measure?.id,
        buildingCurrencyCode: measure?.currencyCode
      }
    });

    if (modelRef.content) {
      modelRef.content.onClose.subscribe((reload: boolean) => {
        if (reload) {
          this.getData(buildingId);
        }
      });
    }
  }

  private removeFromArray(
    array: BuildingMeasureTableItemDTOWithYearData[],
    key: string,
    value: any
  ): BuildingMeasureTableItemDTOWithYearData[] {
    const index = array.findIndex((item: any) => {
      return item[key] === value;
    });
    return index >= 0 ? [...array.slice(0, index), ...array.slice(index + 1)] : [];
  }

  private applyData(): Observable<void> {
    const building = this.building();
    if (!building) {
      return of(undefined);
    }

    this.resetSort.set(false);
    return this.getMeasures(building.id).pipe(
      mergeMap((response) => {
        const measures = response.measureList.map((measure) => measure as BuildingMeasureTableItemDTOWithYearData);

        this.measures().forEach((measure) => {
          const updatedMeasure = measures.find((m) => m.id === measure.id);
          if (updatedMeasure) {
            measure.implementationYear = updatedMeasure.implementationYear;
            measure.status = updatedMeasure.status;
            measure.cols = updatedMeasure.cols;
            measure.investment = updatedMeasure.investment;
            measure.costReduction = updatedMeasure.costReduction;
            measure.co2ConsumptionReduction = updatedMeasure.co2ConsumptionReduction;
            measure.totalEnergyConsumptionReductionInKwh = updatedMeasure.totalEnergyConsumptionReductionInKwh;
            measure.userDefinedInvestment = updatedMeasure.userDefinedInvestment;
            measure.userDefinedCostReduction = updatedMeasure.userDefinedCostReduction;
          }
        });

        this.initMeasures(this.measures());
        return this.getYears();
      }),
      finalize(() => {
        this.saving.set(false);
      })
    );
  }

  private getMeasures(buildingId: number): Observable<MeasureListDTO> {
    const statuses: MeasureStatusDTO[] = this.hasAllMeasureStatuses()
      ? [MeasureStatusDTO.PLANNED, MeasureStatusDTO.OUTOFSCOPE, MeasureStatusDTO.DONE]
      : [MeasureStatusDTO.PLANNED, MeasureStatusDTO.OUTOFSCOPE];
    return this.buildingMeasuresListService.v2BuildingsIdBuildingmeasuresPost({ statuses }, buildingId);
  }

  private sortByCurrentOrder(
    measures: BuildingMeasureTableItemDTOWithYearData[]
  ): BuildingMeasureTableItemDTOWithYearData[] {
    const currentOrder = new Map(this.measures().map((m, i) => [m.id, i]));
    return [...measures].sort((a, b) => {
      const indexA = currentOrder.get(a.id) ?? Number.MAX_SAFE_INTEGER;
      const indexB = currentOrder.get(b.id) ?? Number.MAX_SAFE_INTEGER;
      return indexA - indexB;
    });
  }

  private getData(buildingId: number): void {
    this.getMeasures(buildingId)
      .pipe(
        mergeMap((response) => {
          const measures: BuildingMeasureTableItemDTOWithYearData[] = response.measureList.map((measure) => {
            if (measure.buildingMeasureType === BuildingMeasureTypeDTO.CALCULATED) {
              measure.name = this.translateService.instant(`measure.name.${measure.code}`);
            }
            return { ...measure, cols: {} };
          });

          const plannedAndDoneMeasures = measures.filter((measure) => measure.status !== MeasureStatusDTO.OUTOFSCOPE);
          let measuresOutOfScope = measures.filter((measure) => measure.status === MeasureStatusDTO.OUTOFSCOPE);

          if (this.resetSort()) {
            this.measures.set(this.orderByPipe.transform(plannedAndDoneMeasures, 'co2ConsumptionReduction', true));
            measuresOutOfScope = this.orderByPipe.transform(measuresOutOfScope, 'name');
          } else {
            this.measures.set(this.sortByCurrentOrder(plannedAndDoneMeasures));
          }
          this.measuresOutOfScope.set(measuresOutOfScope);

          this.hasUserDefinedMeasures.set(
            this.measures().some((measure) => measure.buildingMeasureType === BuildingMeasureTypeDTO.USERDEFINED)
          );

          this.initMeasures(this.measures());

          return this.getYears();
        }),
        finalize(() => {
          this.loading.set(false);
          this.saving.set(false);
        })
      )
      .pipe(
        catchError(() => {
          this.errorMessage.set('error.general');
          return of(null);
        })
      )
      .subscribe();
  }

  protected setShowM2(showM2: boolean): void {
    this.showM2.set(showM2);
    this.initMeasures(this.measures());
  }

  private initMeasures(measures: BuildingMeasureTableItemDTOWithYearData[]): void {
    const building = this.building();
    if (!building) {
      return;
    }

    const totals: YearData = {};

    const startYear = this.roadmapService.getStartYear();
    this.hasPlannedMeasures.set(
      measures.some(
        (measure: BuildingMeasureTableItemDTO) => measure.implementationYear && measure.implementationYear >= startYear
      )
    );
    this.hasPlannedMeasuresWithoutYear.set(
      measures.some(
        (measure: BuildingMeasureTableItemDTO) =>
          measure.status === MeasureStatusDTO.PLANNED && !measure.implementationYear
      )
    );

    this.orderByPipe
      .transform([...measures], 'implementationYear')
      .forEach((measure: BuildingMeasureTableItemDTOWithYearData) => {
        let investment = measure.userDefinedInvestment || measure.investment;
        let costReduction = measure.userDefinedCostReduction || measure.costReduction;
        let co2ConsumptionReduction = measure.co2ConsumptionReduction;
        let totalEnergyConsumptionReductionInKwh = measure.totalEnergyConsumptionReductionInKwh;

        if (this.showM2()) {
          totalEnergyConsumptionReductionInKwh /= building.area;
          investment /= building.area;
          costReduction /= building.area;
          co2ConsumptionReduction /= building.area;
        }
        measure.cols = {
          [measure.implementationYear || -1]: {
            investment: investment,
            costReduction: costReduction,
            co2ConsumptionReduction: co2ConsumptionReduction,
            totalEnergyConsumptionReductionInKwh: totalEnergyConsumptionReductionInKwh
          }
        };

        if (
          measure.implementationYear ||
          (measure.status === MeasureStatusDTO.PLANNED && !measure.implementationYear)
        ) {
          let year = measure.implementationYear;
          if (year && !totals[year]) {
            totals[year] = {
              investment: investment,
              costReduction: costReduction,
              co2ConsumptionReduction: co2ConsumptionReduction,
              totalEnergyConsumptionReductionInKwh: totalEnergyConsumptionReductionInKwh
            };
          } else if (year) {
            totals[year].investment += measure.investment;
            totals[year].costReduction += measure.costReduction;
            totals[year].co2ConsumptionReduction += measure.co2ConsumptionReduction;
            totals[year].totalEnergyConsumptionReductionInKwh += totalEnergyConsumptionReductionInKwh;
          }
        }
      });

    this.totals.set(totals);
  }

  private getYears(): Observable<void> {
    this.loadingYears.set(this.saving() ? false : true);

    let getRoadmapYears: Observable<{ details: RoadmapBuildingDetailsDTO; buildingId: number }> | undefined;
    const buildingPublicSharedId = this.buildingPublicSharedId();

    if (buildingPublicSharedId) {
      getRoadmapYears = this.buildingsService
        .v1PublicBuildingsPublicSharedIdGet(buildingPublicSharedId)
        .pipe(
          mergeMap((building) =>
            this.roadmapApiService
              .v2BuildingsIdRoadmapYearsGet(building.id)
              .pipe(map((details) => ({ details, buildingId: building.id })))
          )
        );
    } else {
      const buildingId = this.building()?.id || this.buildingId();
      if (buildingId) {
        getRoadmapYears = this.cachedRoadmapService
          .v2BuildingsIdRoadmapYearsGet(buildingId)
          .pipe(map((details) => ({ details, buildingId })));
      }
    }

    if (!getRoadmapYears) {
      return of(undefined);
    }

    return getRoadmapYears.pipe(
      map(({ details, buildingId }) => {
        details.years.forEach((year) => mapBuildingDetailYearResultsNumbers<RoadmapBuildingYearDTO>(year));
        this.applyDetails(details);
        return buildingId;
      }),
      mergeMap((buildingId) =>
        this.buildingEnergyConsumptionService.v2BuildingsBuildingIdEnergyConsumptionGet(buildingId)
      ),
      map((usages) => {
        this.applyEnergyConsumption(usages);
      })
    );
  }

  private applyDetails(details: RoadmapBuildingDetailsDTO): void {
    const labelGroups = this.labelGroupService.getLabelGroups(details.energyLabels);
    this.labelGroupService.possibleLabelGroups.set(labelGroups);
    const preferredLabelGroup = this.labelGroupService.preferredLabelGroup();
    if (!preferredLabelGroup || !labelGroups.includes(preferredLabelGroup)) {
      const defaultLabelGroup = this.labelGroupService.getDefaultLabelGroup(labelGroups);
      if (defaultLabelGroup) {
        this.labelGroupService.preferredLabelGroup.set(defaultLabelGroup);
      }
    }

    const implementationYears =
      this.measures()
        ?.filter((measure) => measure.implementationYear)
        .map((measure) => measure.implementationYear!) || [];
    const startYear = this.roadmapService.getStartYear(implementationYears);
    const endYear = this.roadmapService.getEndYear(implementationYears);
    // TODO: We should fix the display start year, currently it does not work correctly and the table
    // does not scroll to the correct column, see OSO-22240
    this.displayStartYear.set(startYear);
    details.years = details.years.filter(
      (year: RoadmapBuildingYearDTO) =>
        year.year === null ||
        (year.year && year.year >= startYear && year.year <= endYear) ||
        year.energyLabels?.[LabelGroupDTO.EPC]?.labelType === EnergyLabelTypeDTO.USERDEFINED
    );

    this.years.update((years) =>
      years.map((year: RoadmapBuildingYearDTO & { totalEnergyConsumptionReductionInKwh: number }) => {
        const detailYear = details.years.find((detailYear: RoadmapBuildingYearDTO) => {
          return detailYear.year === year.year;
        });
        if (detailYear) {
          year.energyLabels = detailYear.energyLabels;
        }
        return year;
      })
    );

    this.details.set(details);
  }

  private applyEnergyConsumption(usages: BuildingEnergyConsumptionYearDTO[]): void {
    const startYear: number | null = this.details()?.years[1]?.year || null;
    const startEnergyConsumptionYear = usages.reduce(
      (
        minYear: BuildingEnergyConsumptionYearDTO,
        current: BuildingEnergyConsumptionYearDTO
      ): BuildingEnergyConsumptionYearDTO => {
        if (minYear === null) return current;
        return current.year && minYear.year && current.year < minYear.year ? current : minYear;
      },
      null
    )?.year;

    this.loadingYears.set(false);
    this.startEnergyConsumptionYear.set(
      startEnergyConsumptionYear && startYear
        ? startEnergyConsumptionYear >= startYear
          ? undefined
          : startEnergyConsumptionYear
        : undefined
    );
  }

  protected toggleCompletedMeasures($event: boolean): void {
    const building = this.building();
    if (!building) {
      return;
    }

    this.loading.set(true);
    this.hasAllMeasureStatuses.set($event);
    this.getData(building.id);
  }
}
